import Button from "components/buttons";
import DynamicFormInput from "components/form/form";
import Notes from "components/notes";
import PageTitle from "components/pageTitle";
import TwoColumnLayout from "layouts/TwoColumnLayout";
import { ChangeEvent, useRef, useState } from "react";
import { useToast } from "contexts/ToastContext";
import { LoadingIcon } from "utils/constant";
import FileUpload from "components/FileUpload";
import {
  createRegisterDirective,
  updateRegisterDirective,
} from "services/registerDirectivesService";
import {
  LEGISLATIVE_DRAFTING_REQUEST_STATUS,
  TokenPayload,
} from "utils/constantValues";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "contexts/AuthContext";
import { jwtDecode } from "jwt-decode";
import { useTranslation } from "react-i18next";

const RegisterDirectiveRequest = () => {
  const { showToast } = useToast();
  const [submitting, setSubmitting] = useState(false);
  const navigate = useNavigate();
  const contextData = useAuth();

  const { t } = useTranslation();
  const location = useLocation();
  const editData = location.state?.request;

  const [formState, setFormState] = useState({
    explanatory_notes: editData?.explanatory_notes || "",
    request_letter_reference_number:
      editData?.request_letter_reference_number || "",
    request_letter_files: null as File | null,
    directive_files: null as File | null,
  });

  const [errors, setErrors] = useState<{ [key: string]: string }>({});

  const fileInputRefRequestLetter = useRef<HTMLInputElement>(null);
  const fileInputRefDirective = useRef<HTMLInputElement>(null);

  const handleChange = (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setFormState((prev) => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (
    file: File | null,
    fieldName: keyof Pick<
      typeof formState,
      "request_letter_files" | "directive_files"
    >
  ) => {
    setFormState((prev) => ({ ...prev, [fieldName]: file }));
  };

  const validateForm = () => {
    const newErrors: { [key: string]: string } = {};
    if (!formState.request_letter_files)
      newErrors.request_letter_files = t("directives.letter_required");
    if (!formState.directive_files)
      newErrors.directive_files = t("directives.file_required");
    if (!formState.request_letter_reference_number)
      newErrors.request_letter_reference_number = t(
        "directives.letter_ref_number_required"
      );
    if (!formState.explanatory_notes)
      newErrors.explanatory_notes = t("directives.explanatory_notes_required");

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!validateForm()) {
      showToast(t("directives.fill_all_fields"), "error");
      return;
    }

    setSubmitting(true);
    const data = new FormData();
    Object.keys(formState).forEach((key) => {
      const value = formState[key as keyof typeof formState];
      if (value !== null)
        data.append(key, value instanceof File ? value : String(value));
    });
    data.append("status", LEGISLATIVE_DRAFTING_REQUEST_STATUS.submitted.value);

    const decoded = jwtDecode<TokenPayload>(
      contextData.user.access_token || ""
    );
    data.append(
      "requested_by",
      contextData.user?.user_type === "external"
        ? "Stakeholder"
        : contextData.user?.user_type
    );
    data.append("requested_by_id", contextData.user?.uu_id ?? "");
    data.append("first_name", decoded?.first_name || "");
    data.append("last_name", decoded?.last_name || "");
    data.append("email_user_organization", decoded?.email || "");
    data.append(
      "organization_kebele",
      decoded?.organization?.address?.kebele || ""
    );
    data.append(
      "organization_zone",
      decoded?.organization?.address?.zone || ""
    );
    data.append(
      "organization_state",
      decoded?.organization?.address?.state || ""
    );
    data.append("organization_name", decoded?.organization?.name || "");
    data.append("organization_type", decoded?.organization?.type || "");
    data.append("stakeholder_id", contextData.user?.org_unit_id ?? "");
    try {
      let response;
      if (editData) {
        response = await updateRegisterDirective(contextData.token || contextData?.user?.access_token || "", editData.id, data);
        showToast(
          t("directives.request_success"),
          "success"
        );
      } else {
        response = await createRegisterDirective(contextData.token || contextData?.user?.access_token || "", data);
        showToast(
          t("directives.request_success"),
          "success"
        );
      }
      if (response.id) navigate(`/registering-directives/${response.id}`);

      // Reset file inputs
      if (fileInputRefRequestLetter.current)
        fileInputRefRequestLetter.current.value = "";
      if (fileInputRefDirective.current)
        fileInputRefDirective.current.value = "";
    } catch (error) {
      console.error(error);
      showToast(t("directives.request_failed"), "error");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div>
      <PageTitle
        icon="📄" 
        text={t("RegisterDirective")}
        subtitle={t("directives.submit_request_desc_alt")}
        iconColor="text-purple-600"
        textColor="text-[#432101]"
      />
      <form onSubmit={handleSubmit}>
        <TwoColumnLayout
          left={
            <div className="py-[22px] px-[24px] bg-white rounded-xl space-y-4">
              <DynamicFormInput
                inputs={[
                  {
                    value: formState.request_letter_reference_number,
                    type: "text",
                    label: t("directives.letter_ref_number_label") + " *",
                    name: "request_letter_reference_number",
                    onChange: handleChange,
                    placeholder: t("directives.letter_ref_number_placeholder"),
                    error: errors.request_letter_reference_number,
                  },
                  {
                    value: formState.explanatory_notes,
                    type: "textarea",
                    label: t("directives.explanatory_notes_label") + " *",
                    name: "explanatory_notes",
                    onChange: handleChange,
                    placeholder: t("DetailHere"),
                    error: errors.explanatory_notes,
                  },
                ]}
              />

              <div className="flex flex-col md:flex-row gap-6">
                <div className="flex-1">
                  <FileUpload
                    label={t("RequestLetter") + " *"}
                    onChange={(file) =>
                      handleFileChange(file, "request_letter_files")
                    }
                  />
                  {errors.request_letter_files && (
                    <span className="text-red-500 text-sm">
                      {errors.request_letter_files}
                    </span>
                  )}
                </div>
                <div className="flex-1">
                  <FileUpload
                    label={t("DirectiveFile") + " *"}
                    onChange={(file) =>
                      handleFileChange(file, "directive_files")
                    }
                  />
                  {errors.directive_files && (
                    <span className="text-red-500 text-sm">
                      {errors.directive_files}
                    </span>
                  )}
                </div>
              </div>

              <div className="flex space-x-4 justify-between mt-4">
                <Button
                  variant="secondary"
                  onClick={() => {
                    if (fileInputRefRequestLetter.current)
                      fileInputRefRequestLetter.current.value = "";
                    if (fileInputRefDirective.current)
                      fileInputRefDirective.current.value = "";
                    navigate(-1);
                  }}
                >
                  Cancel
                </Button>
                <Button type="submit" variant="primary" onClick={() => {}}>
                  {submitting ? (
                    <div className="flex items-center justify-center">
                      {LoadingIcon} {t("Submitting")}...
                    </div>
                  ) : (
                    t("Submit")
                  )}
                </Button>
              </div>
            </div>
          }
          right={
            <div className="w-full max-w-md space-y-4">
              <Notes
                title={t("Note")}
                text={t("directives_notes.note_text")}
                subtext={t("directives_notes.note_subtext")}
              />
              <div className="bg-white rounded-xl shadow-sm p-4 space-y-2">
                <h2 className="font-semibold">{t("directives_notes.requirements_title")}</h2>
                <p className="text-sm text-gray-500">{t("directives_notes.requirements_desc")}</p>
                <ul className="list-disc list-inside text-sm text-gray-600">
                  <li>{t("RequestLetter")}</li>
                  <li>{t("DirectiveFile")}</li>
                  <li>{t("SupportingAttachments")}</li>
                </ul>
              </div>
            </div>
          }
        />
      </form>
    </div>
  );
};

export default RegisterDirectiveRequest;
